/**
 * EUDR Polygon Validator Component
 *
 * Technical validation system for polygon geometry and EUDR compliance.
 * Based on FAO thresholds and EU Regulation 2023/1115 requirements.
 *
 * Validates:
 * - Topology (self-intersection, closure, vertex count)
 * - Coordinate Reference System (WGS84 EPSG:4326)
 * - 4-hectare rule compliance
 * - Edge effects buffer
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    CheckCircle,
    XCircle,
    AlertTriangle,
    Info,
    Layers,
    Loader2,
} from 'lucide-react';
import { validationApi, getErrorMessage } from '../../lib/api';

interface PolygonValidatorProps {
    polygonId: string;
}

interface ValidationResult {
    passed: boolean;
    message: string;
    severity: 'success' | 'warning' | 'error';
    details?: string;
}

interface TopologyValidation {
    is_valid: boolean;
    has_self_intersection: boolean;
    is_closed: boolean;
    vertex_count: number;
    simplified_vertices: number;
    precision_loss_meters: number;
}

export default function PolygonValidator({ polygonId }: PolygonValidatorProps) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [validationData, setValidationData] = useState<any>(null);

    // Fetch validation data and polygon geometry from API
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch validation data
                const validation = await validationApi.validatePolygon(polygonId, {
                    include_baseline: false,
                    include_fao_thresholds: false,
                    include_edge_effects: true,
                    buffer_distance_m: -7.5,
                });
                setValidationData(validation);
            } catch (err: any) {
                console.error('Validation error:', err);
                setError(getErrorMessage(err) || t('validation.errorValidating'));
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [polygonId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-96">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">{t('validation.validatingPolygon')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card p-6">
                <div className="flex items-start gap-3">
                    <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            {t('validation.validationError')}
                        </h3>
                        <p className="text-red-600">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!validationData) {
        return null;
    }

    // Map API response to component data structure
    const topology: TopologyValidation = {
        is_valid: validationData.topology.is_valid,
        has_self_intersection: !validationData.topology.is_simple,
        is_closed: validationData.topology.is_closed,
        vertex_count: validationData.topology.vertex_count,
        simplified_vertices: validationData.topology.vertex_count,
        precision_loss_meters: 0,
    };

    const validations: ValidationResult[] = [
        {
            passed: topology.is_valid,
            message: t('validation.checks.topologyValidation'),
            severity: topology.is_valid ? 'success' : 'error',
            details: topology.is_valid ? t('validation.checks.topologyPass') : t('validation.checks.topologyFail'),
        },
        {
            passed: validationData.geometry.is_wgs84,
            message: t('validation.checks.wgs84Compliance'),
            severity: validationData.geometry.is_wgs84 ? 'success' : 'error',
            details: validationData.geometry.is_wgs84 ? t('validation.checks.wgs84Pass') : t('validation.checks.wgs84Fail', { crs: validationData.geometry.crs }),
        },
        {
            passed: validationData.geometry.area_ha >= 4,
            message: t('validation.checks.fourHectareRule'),
            severity: validationData.geometry.area_ha >= 4 ? 'success' : 'warning',
            details: validationData.geometry.area_ha >= 4 ? t('validation.checks.fourHectarePass') : t('validation.checks.fourHectareFail'),
        },
    ];

    const passedCount = validations.filter((v) => v.passed).length;
    const totalCount = validations.length;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: EUDR Compliance Validation Summary */}
            <div className="card p-6">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            {t('validation.title')}
                        </h3>
                        <p className="text-sm text-gray-500">
                            {t('validation.description')}
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-3xl font-bold text-gray-900 dark:text-white">
                            {passedCount}/{totalCount}
                        </div>
                        <p className="text-sm text-gray-500">{t('validation.checksPassed')}</p>
                    </div>
                </div>

                {/* Validation Checks */}
                <div className="space-y-3">
                    {validations.map((validation, index) => (
                        <div
                            key={index}
                            className={`flex items-center gap-3 p-3 rounded-lg ${
                                validation.severity === 'success'
                                    ? 'bg-green-50 dark:bg-green-900/20'
                                    : validation.severity === 'warning'
                                    ? 'bg-yellow-50 dark:bg-yellow-900/20'
                                    : 'bg-red-50 dark:bg-red-900/20'
                            }`}
                        >
                            {validation.severity === 'success' ? (
                                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                            ) : validation.severity === 'warning' ? (
                                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                            ) : (
                                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                            )}
                            <div className="flex-1">
                                <p
                                    className={`font-medium ${
                                        validation.severity === 'success'
                                            ? 'text-green-900 dark:text-green-100'
                                            : validation.severity === 'warning'
                                            ? 'text-yellow-900 dark:text-yellow-100'
                                            : 'text-red-900 dark:text-red-100'
                                    }`}
                                >
                                    {validation.message}
                                </p>
                                {validation.details && (
                                    <p
                                        className={`text-sm ${
                                            validation.severity === 'success'
                                                ? 'text-green-700 dark:text-green-300'
                                                : validation.severity === 'warning'
                                                ? 'text-yellow-700 dark:text-yellow-300'
                                                : 'text-red-700 dark:text-red-300'
                                        }`}
                                    >
                                        {validation.details}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right: Topology Validation Details */}
            <div className="card p-6">
                <div className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                        <Layers className="w-5 h-5 text-primary-600" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {t('validation.topology.title')}
                        </h3>
                    </div>
                    <p className="text-sm text-gray-500">
                        {t('validation.topology.description')}
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t('validation.topology.selfIntersection')}
                            </p>
                            {!topology.has_self_intersection ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                                <XCircle className="w-5 h-5 text-red-600" />
                            )}
                        </div>
                        <p className="text-xs text-gray-500">
                            {t('validation.topology.noSelfIntersection')}
                        </p>
                    </div>

                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t('validation.topology.closure')}
                            </p>
                            {topology.is_closed ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                                <XCircle className="w-5 h-5 text-red-600" />
                            )}
                        </div>
                        <p className="text-xs text-gray-500">
                            {t('validation.topology.closureDescription')}
                        </p>
                    </div>

                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="mb-3">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('validation.topology.vertexCount')}
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {topology.vertex_count}
                            </p>
                        </div>
                        <p className="text-xs text-gray-500">
                            {t('validation.topology.vertexCountDescription')}
                        </p>
                    </div>

                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="mb-3">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('validation.topology.simplifiedVertices')}
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {topology.simplified_vertices}
                            </p>
                        </div>
                        <p className="text-xs text-gray-500">
                            {t('validation.topology.simplifiedDescription')}
                        </p>
                    </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex gap-3">
                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-900 dark:text-blue-100">
                            <p className="font-semibold mb-1">{t('validation.topology.precisionLoss')}</p>
                            <p className="text-blue-700 dark:text-blue-300">
                                {t('validation.topology.precisionLossDescription', { meters: topology.precision_loss_meters.toFixed(2) })}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
